<template>
  <div>
    <div class="mb-3">
      <label>Dimensions</label>
      <b-form-checkbox
          size="sm"
          v-model="allDimensions"
          :value="true"
          :unchecked-value="false"
          @change="selectAllDimensions"
      >
        {{ allDimensions ? 'Deselect' : 'Select' }} All
      </b-form-checkbox>
      <treeselect v-model="selectedDimensions" :multiple="true" :options="dimensionOptions" @input="emitChanges">
        <template v-slot:option-label="{ node }" :class="labelClassName">
          <span style="font-size: 0.85em" class="ml-1" :title="node.label">{{ node.label }}</span>
        </template>
      </treeselect>
    </div>
    <div class="mb-3">
      <label>Measures</label>
      <b-form-checkbox
          size="sm"
          v-model="allMeasures"
          :value="true"
          :unchecked-value="false"
          @change="selectAllMeasures"
      >
        {{ allMeasures ? 'Deselect' : 'Select' }} All
      </b-form-checkbox>
      <treeselect v-model="selectedMeasures" :multiple="true" :options="measureOptions" @input="emitChanges">
        <template v-slot:option-label="{ node }" :class="labelClassName">
          <span style="font-size: 0.85em" class="ml-1" :title="node.label">{{ node.label }}</span>
        </template>
      </treeselect>
      <b-button class="mt-3 mb-2" variant="outline-primary" size="sm"
                @click="showAddMeasureModal">
        Add measure
      </b-button>
      <div v-if="selectedSpecialMeasures.length" class="border rounded p-1">
        <div v-for="measure in selectedSpecialMeasures" :key="measure.name + measure.aggregateFunction"
             class="m-1 vue-treeselect__multi-value-item">
          <span class="vue-treeselect__multi-value-label">{{ `${measure.name} (${measure.aggregateFunction})` }}</span>
          <span class="vue-treeselect__icon vue-treeselect__value-remove"
                @click="removeSpecialMeasure(measure)"
          >
            <strong>x</strong>
          </span>
        </div>
      </div>
    </div>

    <b-modal ref="add-measure-modal" hide-header button-size="sm" centered @ok.prevent="addSpecialMeasure">
      <b-form ref="add-measure-form" @submit.prevent="addSpecialMeasure">
        <b-form-group label="Aggregate function">
          <treeselect v-model="selectedAggregateFunction" :options="aggregateFunctions" @input="aggregateChanged"
                      required/>
        </b-form-group>
        <b-form-group label="Measure">
          <treeselect v-model="selectedSpecialMeasure" :options="columnsForAggregationOptions" required>
            <template v-slot:option-label="{ node }" :class="labelClassName">
              <span style="font-size: 0.85em" class="ml-1" :title="node.label">{{ node.label }}</span>
            </template>
          </treeselect>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import _ from "lodash";
import Treeselect from "@riophae/vue-treeselect";

export default {
  name: "BqFilterFields",
  components: {Treeselect},
  props: {
    value: {
      type: Array,
    },
    columns: {
      type: Array
    }
  },
  mounted() {
    this.setValue();
  },
  watch: {
    value: {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.setValue();
        }
      }
    },
  },
  data() {
    return {
      selectedDimensions: [],
      selectedMeasures: [],
      selectedSpecialMeasures: [],

      allDimensions: false,
      allMeasures: false,

      selectedSpecialMeasure: '',
      selectedAggregateFunction: '',

      aggregateFunctions: [
        {id: 'avg', label: 'Avg'},
        {id: 'min', label: 'Min'},
        {id: 'max', label: 'Max'},
        {id: 'count', label: 'Count'},
        {id: 'countDistinctApprox', label: 'Count Distinct (Approx)'},
        {id: 'countDistinctExact', label: 'Count Distinct (Exact)'},
      ]
    }
  },
  computed: {
    dimensionOptions() {
      return _.chain(this.columns)
          .filter({columnType: 'dimension'})
          .map((c) => {
            return {id: c.name, label: c.name}
          })
          .orderBy(['label'])
          .value();
    },
    measureOptions() {
      return _.chain(this.columns)
          .filter({columnType: 'measure'})
          .map((c) => {
            return {id: c.name, label: `${c.name}(sum)`}
          })
          .orderBy(['label'])
          .value();
    },
    columnsForAggregationOptions() {
      switch (this.selectedAggregateFunction) {
        case 'count':
        case 'countDistinctApprox':
        case 'countDistinctExact':
          return _.chain(this.columns)
              .filter({columnType: 'dimension'})
              .map((c) => {
                return {id: c.name, label: `${c.name}(${this.selectedAggregateFunction})`}
              })
              .orderBy(['label'])
              .value();
        default:
          return _.chain(this.columns)
              .filter({columnType: 'measure'})
              .map((c) => {
                return {id: c.name, label: `${c.name}(${this.selectedAggregateFunction})`}
              })
              .orderBy(['label'])
              .value();
      }
    }
  },
  methods: {
    setValue() {

      if (this.value) {
        this.selectedDimensions = _.chain(this.value)
            .filter((c) => {
              return c.columnType === 'dimension' && (!c.aggregateFunction)
            })
            .map((c) => {
              return c.name
            })
            .value();
        this.selectedMeasures = _.chain(this.value)
            .filter((c) => {
              return c.columnType === 'measure' && (!c.aggregateFunction)
            })
            .map((c) => {
              return c.name
            })
            .value();
      }
    },
    aggregateChanged() {
      if (!_.some(this.columnsForAggregationOptions, {id: this.selectedSpecialMeasure}))
        this.selectedSpecialMeasure = this.columnsForAggregationOptions[0].id;
    },
    selectAllDimensions(v) {
      if (v) {
        this.selectedDimensions = _.map(this.dimensionOptions, (c) => {
          return c.id
        });
      } else {
        this.selectedDimensions = [];
      }
      this.emitChanges();
    },
    selectAllMeasures(v) {
      if (v) {
        this.selectedMeasures = _.map(this.measureOptions, (c) => {
          return c.id
        });
      } else {
        this.selectedMeasures = [];
      }
      this.emitChanges();
    },

    showAddMeasureModal() {
      this.selectedSpecialMeasure = this.measureOptions[0].id;
      this.selectedAggregateFunction = this.aggregateFunctions[0].id;
      this.$refs['add-measure-modal'].show();
    },
    addSpecialMeasure() {
      const form = this.$refs['add-measure-form'];
      if (!form.reportValidity()) {
        return;
      }
      if (!_.some(this.selectedSpecialMeasures, {
        name: this.selectedSpecialMeasure,
        aggregateFunction: this.selectedAggregateFunction
      })) {
        let measure = _.find(this.columns, {name: this.selectedSpecialMeasure});
        this.selectedSpecialMeasures.push({
          ...measure, ...{
            columnType: 'measure',
            aggregateFunction: this.selectedAggregateFunction
          }
        });
      }

      this.$refs['add-measure-modal'].hide();
      this.emitChanges();
    },
    removeSpecialMeasure(measure) {
      this.selectedSpecialMeasures.splice(this.selectedSpecialMeasures.findIndex(v => v === measure), 1);
      this.emitChanges();
    },

    emitChanges() {
      let dimensions = _.filter(this.columns, (d) => {
        return this.selectedDimensions.includes(d.name);
      });
      let measures = _.filter(this.columns, (d) => {
        return this.selectedMeasures.includes(d.name);
      });
      this.$emit('input', [...dimensions, ...measures, ...this.selectedSpecialMeasures]);
    }
  }
}
</script>

<style scoped>
.column-badge {
  cursor: pointer;
  padding: 0 0.5em 0 2em;
  margin-bottom: 0.2em;
}
</style>